import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
  EntityState,
  createSelector,
} from "@reduxjs/toolkit";
import { RootState } from "../..";
import {
  Project,
  ProjectTag,
  ProjectImage,
  ProjectImageRelation,
  ProjectTagRelation,
  projectsAPI,
  projects,
  tags,
  tagRelations,
  imageRelations,
  images,
} from "./projectsAPI";
import { EntityId } from "@reduxjs/toolkit";
import { generateEntityStateFromArray } from "../../store";

const projectsAdapter = createEntityAdapter<Project>();
const projectsInitialState = projectsAdapter.getInitialState(
  generateEntityStateFromArray(projects)
);

const projectTagsAdapter = createEntityAdapter<ProjectTag>();
const projectTagsInitialState = projectTagsAdapter.getInitialState(
  generateEntityStateFromArray(tags)
);

const projectTagRelationsAdapter = createEntityAdapter<ProjectTagRelation>();
const projectTagRelationsInitiialState = projectTagRelationsAdapter.getInitialState(
  generateEntityStateFromArray(tagRelations)
);

const projectImagesAdapter = createEntityAdapter<ProjectImage>();
const projectImagesInitialState = projectImagesAdapter.getInitialState(
  generateEntityStateFromArray(images)
);

const projectImageRelationsAdapter = createEntityAdapter<ProjectImageRelation>();
const projectImageRelationsInitialState = projectImageRelationsAdapter.getInitialState(
  generateEntityStateFromArray(imageRelations)
);
export const fetchProjectsData = createAsyncThunk(
  "projects/fetchProjectsData",
  projectsAPI.fetchData
);

interface InitialState extends EntityState<Project> {
  tags: typeof projectTagsInitialState;
  tagRelations: typeof projectTagRelationsInitiialState;
  images: typeof projectImagesInitialState;
  imageRelations: typeof projectImageRelationsInitialState;
  status: "pending" | "fulfilled" | "rejected";
}

const initialState: InitialState = {
  ...projectsInitialState,
  tags: projectTagsInitialState,
  tagRelations: projectTagRelationsInitiialState,
  images: projectImagesInitialState,
  imageRelations: projectImageRelationsInitialState,
  status: "fulfilled",
};

export const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchProjects
    builder
      .addCase(fetchProjectsData.fulfilled, (state, action) => {
        if (action.payload.projects.length) {
          projectsAdapter.removeAll(state);
          projectsAdapter.addMany(state, action.payload.projects);
        }

        if (action.payload.images.length) {
          projectImagesAdapter.removeAll(state.images);
          projectImagesAdapter.addMany(state.images, action.payload.images);
        }

        if (action.payload.imageRelations.length) {
          projectImageRelationsAdapter.removeAll(state.imageRelations);
          projectImageRelationsAdapter.addMany(
            state.imageRelations,
            action.payload.imageRelations
          );
        }

        if (action.payload.tags.length) {
          projectTagsAdapter.removeAll(state.tags);
          projectTagsAdapter.addMany(state.tags, action.payload.tags);
        }

        if (action.payload.tagRelations.length) {
          projectTagRelationsAdapter.removeAll(state.tagRelations);
          projectTagRelationsAdapter.addMany(
            state.tagRelations,
            action.payload.tagRelations
          );
        }

        state.status = "fulfilled";
      })
      .addCase(fetchProjectsData.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchProjectsData.rejected, (state) => {
        state.status = "rejected";
      });
  },
});

export const projectsSelectors = projectsAdapter.getSelectors<RootState>(
  (state) => state.projects
);
export const tagsSelectors = projectTagsAdapter.getSelectors<RootState>(
  (state) => state.projects.tags
);
export const tagRelationsSelectors = projectTagRelationsAdapter.getSelectors<RootState>(
  (state) => state.projects.tagRelations
);
export const imagesSelectors = projectImagesAdapter.getSelectors<RootState>(
  (state) => state.projects.images
);
export const imageRelationsSelectors = projectImageRelationsAdapter.getSelectors<RootState>(
  (state) => state.projects.imageRelations
);

export const selectTagsByProjectID = createSelector(
  [
    tagsSelectors.selectEntities,
    tagRelationsSelectors.selectAll,
    (state: RootState, projectID: EntityId) => projectID,
  ],
  (tagsSrc, tagRels, projectID) => {
    const tags: ProjectTag[] = [];
    for (const tagRel of tagRels) {
      if (tagRel?.projectID === projectID) {
        const got = tagsSrc[tagRel.tagID];
        if (got) {
          tags.push(got);
        }
      }
    }
    return tags;
  }
);

export const selectImagesByProjectID = createSelector(
  [
    imagesSelectors.selectEntities,
    imageRelationsSelectors.selectAll,
    (state: RootState, projectID: EntityId) => projectID,
  ],
  (imageSrcs, imageRels, projectID) => {
    const images: ProjectImage[] = [];
    for (const imgRel of imageRels) {
      if (imgRel?.projectID === projectID) {
        const got = imageSrcs[imgRel.imageID];
        if (got) {
          images.push(got);
        }
      }
    }
    return images;
  }
);

export const projectsActions = projectsSlice.actions;
export const projectsReducer = projectsSlice.reducer;
